import { useEffect, useEffectEvent, useRef, useCallback, useState } from 'react';
import { SignalRService } from '@/common/infrastructure/services/SignalRService';
import { useSignalRConnection } from '@/common/presentation/hooks/useSignalRConnection';
import { GpsPositionUpdateService, PositionChangedNotification } from '@/map/application/services/GpsPositionUpdateService';
import { generateMarkersKey } from '@/map/application/utils/markerUtils';
import { Marker, Position } from '@/map/domain/entities/map.types';
import useAuth from '@/auth/application/useAuth';

/**
 * GPS SignalR method constants
 */
export const GPS_SIGNALR_METHODS = {
  POSITION_CHANGED: 'PositionChangedNotification'
} as const;

/**
 * Hook to manage GPS position updates via SignalR
 * Uses the main SignalR connection (clienthub) and listens for PositionChangedNotification events
 *
 * @param enabled Whether GPS updates are enabled (default: true)
 * @param onPositionUpdate Optional callback when a position is updated
 * @returns Connection state and update service
 */
const isSignalRDisabled = import.meta.env.VITE_DISABLE_SIGNALR === 'true';

export const useGpsPositionUpdates = (enabled = true, onPositionUpdate?: (id: string | number, position: Position) => void) => {
  const { user } = useAuth();
  const currentTenantDbName = user?.dbName;
  const gpsUpdateServiceRef = useRef<GpsPositionUpdateService | null>(null);
  const signalRServiceRef = useRef<SignalRService | null>(null);
  const positionUpdateCallbackRef = useRef(onPositionUpdate);

  // Keep callback ref up to date
  positionUpdateCallbackRef.current = onPositionUpdate;

  // Stable callback for position updates
  const stableOnPositionUpdate = useEffectEvent((id: string | number, position: Position) => {
    positionUpdateCallbackRef.current?.(id, position);
  });

  // Use shared SignalR connection hook (CORS error handling is centralized here)
  const shouldEnableSignalR = enabled && !isSignalRDisabled;
  const {
    connectionState,
    isInitialized,
    error: connectionError,
    signalRService
  } = useSignalRConnection({
    enabled: shouldEnableSignalR
  });

  // Initialize service synchronously when signalRService is available
  if (signalRService && !gpsUpdateServiceRef.current) {
    gpsUpdateServiceRef.current = new GpsPositionUpdateService();
  }

  // Keep service refs up to date
  useEffect(() => {
    signalRServiceRef.current = signalRService;
    if (signalRService && !gpsUpdateServiceRef.current) {
      gpsUpdateServiceRef.current = new GpsPositionUpdateService();
    }
  }, [signalRService]);

  // Subscribe to GPS position updates when connected
  useEffect(() => {
    if (!shouldEnableSignalR || !signalRService || !isInitialized || !connectionState.isConnected) {
      return;
    }

    const handlePositionNotification = (...args: unknown[]) => {
      try {
        const notification = args[0] as PositionChangedNotification;
        gpsUpdateServiceRef.current?.processPositionUpdate(notification, currentTenantDbName);

        if (notification.id != null && notification.latitude != null && notification.longitude != null) {
          const position: Position = {
            lat: Number(notification.latitude),
            lng: Number(notification.longitude)
          };
          stableOnPositionUpdate(notification.id, position);
        }
      } catch (err) {
        console.error('Error handling PositionChangedNotification:', err);
      }
    };

    signalRService.on(GPS_SIGNALR_METHODS.POSITION_CHANGED, handlePositionNotification);

    return () => {
      signalRService.off(GPS_SIGNALR_METHODS.POSITION_CHANGED, handlePositionNotification);
    };
  }, [shouldEnableSignalR, signalRService, isInitialized, connectionState.isConnected, currentTenantDbName, stableOnPositionUpdate]);

  return {
    connectionState,
    isInitialized,
    error: connectionError,
    isConnected: connectionState.isConnected,
    connectionId: connectionState.connectionId,
    gpsUpdateService: gpsUpdateServiceRef.current
  };
};

/**
 * Hook to update markers in real-time based on GPS position updates
 *
 * @param markers Current markers array
 * @param enabled Whether GPS updates are enabled
 * @returns Updated markers array and loading state
 */
export const useMarkersWithGpsUpdates = (markers: Marker[], enabled = true) => {
  const [updatedMarkers, setUpdatedMarkers] = useState<Marker[]>(markers);
  const [isUpdating, setIsUpdating] = useState(false);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gpsUpdateServiceRef = useRef<GpsPositionUpdateService | null>(null);
  const previousMarkersRef = useRef<Marker[]>(markers);
  const previousMarkersKeyRef = useRef<string>(generateMarkersKey(markers));

  // Stable callback for position updates
  const handlePositionUpdate = useCallback((id: string | number, position: Position) => {
    setIsUpdating(true);
    setUpdatedMarkers((prev: Marker[]) => {
      if (!gpsUpdateServiceRef.current) return prev;
      return gpsUpdateServiceRef.current.updateMarkerPosition(prev, id, position);
    });

    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      setIsUpdating(false);
      updateTimeoutRef.current = null;
    }, 100);
  }, []);

  const { gpsUpdateService } = useGpsPositionUpdates(enabled, handlePositionUpdate);

  // Keep service ref up to date
  useEffect(() => {
    gpsUpdateServiceRef.current = gpsUpdateService;
  }, [gpsUpdateService]);

  // Sync markers when input markers change (content-based comparison)
  useEffect(() => {
    if (previousMarkersRef.current === markers) {
      return; // Same reference, no change
    }

    const currentKey = generateMarkersKey(markers);
    if (currentKey !== previousMarkersKeyRef.current) {
      previousMarkersKeyRef.current = currentKey;
      previousMarkersRef.current = markers;
      setUpdatedMarkers(markers);
    } else {
      previousMarkersRef.current = markers; // Update ref to avoid re-checking
    }
  }, [markers]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, []);

  return {
    markers: updatedMarkers,
    isUpdating
  };
};
